import { FC, useState } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { wrapLabel } from 'src/pages/Campaigns/CampaignComparison/MultiComparison/wrapLabel'
import { cn } from 'src/utils/cn'
import styles from './commentVolume.module.scss'
import { PlatformBreakdown } from './PlatformBreakdown'
import {
  CommentPlatformRow,
  CommentSentiment,
  ContentCampaign,
  TopComment,
} from '../interfaces'

interface CommentVolumeProps {
  byPlatform: { platforms: string[]; rows: CommentPlatformRow[] }
  bySentiment: CommentSentiment[]
  campaigns: ContentCampaign[]
  insight: string
  topComments: TopComment[]
}

const COPY = {
  byPlatform: 'By platform',
  bySentiment: 'By sentiment',
  insight: 'Key Insight',
  subtitle: 'Sentiment breakdown',
  title: 'Comment Volume',
  topComments: 'Top Comments',
}

// Bottom to top, the way the bars stack.
const BANDS = [
  { color: '#8080ff', key: 'positive', label: 'Positive' },
  { color: '#c7ced1', key: 'neutral', label: 'Neutral' },
  { color: '#fa7138', key: 'negative', label: 'Negative' },
] as const

type BandKey = (typeof BANDS)[number]['key']
type Mode = 'platform' | 'sentiment'

const CHART_HEIGHT = 383
const CHART_WIDTH = 780
const AXIS_STEPS = 5

// A band shorter than this cannot hold its two lines without printing them
// across the band below.
const LABEL_FITS = 34

const formatCount = (value: number) => value.toLocaleString('en-US')

// Four lines of a 12px comment is about the depth the design gives a card
// without one long comment making the rail scroll for the rest.
const COMMENT_LINES = 4

// Authors arrive from the collector padded — ' kaishauri22', 'lala.tkhelidze   '
// — so the initial was a space and the avatar drew a blank circle. Trimmed
// here rather than trusting the field; the padding is in the stored comment.
const authorOf = (author: string) => author.trim()

// Percentages and point-changes, which the design sets apart from the prose so
// the numbers can be found without reading the sentence. Dates and plain
// counts are deliberately not matched — the design leaves those in the run of
// the text.
//
// Two copies of the same pattern: `split` needs the global flag to keep the
// separators, and `test` on a global regex carries `lastIndex` between calls,
// which would make it answer differently for identical strings.
const FIGURE_SPLIT = /(\d[\d,.]*%|\d+-point)/g
const FIGURE = /^(?:\d[\d,.]*%|\d+-point)$/

// The step is rounded so the ticks read 0, 100, 200 rather than 0, 60.6.
const axisStep = (tallest: number) => {
  const rough = Math.max(1, tallest) / AXIS_STEPS
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const nice = [1, 1.5, 2, 2.5, 3, 5, 10].find(
    (step) => step * magnitude >= rough
  )

  return (nice ?? 10) * magnitude
}

// The axis ends on the first round step PAST the tallest stack, not on a whole
// five of them. Five steps of 100 took a tallest of 303 up to 500, so the
// column stood at 61% of the plot and every shorter one was squashed into the
// floor — which is what made the small campaigns look hidden.
const axisMax = (tallest: number) => {
  if (tallest <= 0) return AXIS_STEPS

  const step = axisStep(tallest)

  return step * Math.ceil(tallest / step)
}

/*
 * Comment Volume: the same stacked chart under two questions.
 *
 * "By sentiment" splits each campaign's comments three ways; "By platform"
 * splits them by where they were left. One chart, one axis, one switch — the
 * shapes are identical, so the widget swaps its series rather than its layout.
 *
 * Beside it, the comments themselves: a chart says how many and a quote says
 * what they were, and the design puts both on one card because neither answers
 * the other's question.
 */
export const CommentVolume: FC<CommentVolumeProps> = ({
  byPlatform,
  bySentiment,
  campaigns,
  insight,
  topComments,
}) => {
  const [mode, setMode] = useState<Mode>('sentiment')

  // Ordered by the campaigns, not by the rows: a row missing for a campaign
  // would otherwise shift every stack after it under the wrong name.
  const sentimentRows = campaigns.map((campaign) =>
    bySentiment.find((row) => row.campaignId === campaign.id)
  )

  const totals = sentimentRows.map((row) =>
    row ? row.negative + row.neutral + row.positive : 0
  )

  const top = axisMax(Math.max(...totals, 0))

  // Both flanks carry the axis, as the design draws it: with four stacks the
  // right-hand column is a long way from the left one.
  const valueAxis = (showSplitLine: boolean): EChartOption.YAxis => ({
    axisLabel: {
      color: '#000000b2',
      fontFamily: 'Roboto, sans-serif',
      fontSize: 12,
      formatter: (value: number) => formatCount(value),
    },
    axisLine: { lineStyle: { color: '#e6ebfa' }, show: true },
    axisTick: { show: false },
    interval: axisStep(Math.max(...totals, 0)),
    max: top,
    min: 0,
    splitLine: {
      lineStyle: { color: '#e6ebfa', type: 'dashed' as const },
      show: showSplitLine,
    },
    type: 'value',
  })

  const share = (value: number, total: number) =>
    total ? Math.round((value / total) * 100) : 0

  // Which band actually sits on top of each column. Capping 'negative'
  // unconditionally left a square top wherever a column had no negatives —
  // which is most of them once an analysis is doing well.
  const topBandFor = (row: (typeof sentimentRows)[number]) => {
    if (!row) return null

    return (
      [...BANDS].reverse().find((band) => row[band.key as BandKey] > 0)?.key ??
      null
    )
  }

  const sentimentSeries = BANDS.map((band) => ({
    barWidth: 88,
    data: sentimentRows.map((row) => ({
      itemStyle: {
        color: band.color,
        // Only the topmost band is capped, so the stack reads as one column
        // with a cap rather than three stacked pills.
        ...(topBandFor(row) === band.key
          ? { barBorderRadius: [8, 8, 0, 0] }
          : {}),
      },
      value: row ? row[band.key as BandKey] : 0,
    })),
    itemStyle: {
      color: band.color,
    },
    label: {
      // `value`, not `data`: each point carries its own itemStyle now, so
      // `data` is the whole object and printed as "[object Object]".
      formatter: (params: { dataIndex: number; value: number }) => {
        const total = totals[params.dataIndex]
        const height = top ? (params.value / top) * (CHART_HEIGHT - 90) : 0

        return height < LABEL_FITS
          ? ''
          : `{share|${share(params.value, total)}%}\n{value|${formatCount(
              params.value
            )}}`
      },
      position: 'inside' as const,
      // Both lines are Inter at #000000b2, as the design specifies. The line
      // heights are 100% of the size, so the pair sits tight; the gap between
      // them is the block's own.
      rich: {
        share: {
          color: '#000000b2',
          fontFamily: 'Inter, sans-serif',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 14,
        },
        value: {
          color: '#000000b2',
          fontFamily: 'Inter, sans-serif',
          fontSize: 10,
          fontWeight: 400,
          lineHeight: 12,
        },
      },
      show: true,
    },
    name: band.label,
    stack: 'comments',
    type: 'bar' as const,
  }))

  const options: EChartOption = {
    grid: { bottom: 48, containLabel: true, left: 8, right: 8, top: 16 },
    legend: {
      bottom: 0,
      itemGap: 20,
      itemHeight: 10,
      itemWidth: 10,
      textStyle: {
        color: '#25262d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
      },
      type: 'plain',
    },
    series: sentimentSeries,
    tooltip: {
      axisPointer: { type: 'none' },
      backgroundColor: '#ffffff',
      borderWidth: 0,
      extraCssText:
        'border-radius:12px;padding:14px 16px;' +
        'box-shadow:0 10px 30px rgba(37,38,45,0.16);',
      // The stack's own colours and counts, which the bands cannot all print
      // when one of them is too short to hold two lines.
      formatter: (params) => {
        const entries = Array.isArray(params) ? params : [params]

        const rows = entries
          .map(
            (entry) =>
              `<div style="display:flex;align-items:center;gap:10px;` +
              `margin-top:10px;font-family:Inter,sans-serif;font-size:14px;">` +
              `<span style="width:10px;height:10px;border-radius:50%;` +
              `background:${entry.color};flex-shrink:0;"></span>` +
              `<span style="color:#25262d;">${entry.seriesName}</span>` +
              `<b style="margin-left:auto;padding-left:24px;color:#25262d;` +
              `font-weight:600;">${formatCount(Number(entry.value))}</b>` +
              `</div>`
          )
          .join('')

        return (
          `<div style="font-family:Inter,sans-serif;font-size:14px;` +
          `color:#25262d;">${entries[0]?.name ?? ''}</div>${rows}`
        )
      },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        formatter: (name: string) => wrapLabel(name, 26),
        margin: 14,
      },
      axisLine: { lineStyle: { color: '#e6ebfa' } },
      axisTick: { show: false },
      data: campaigns.map((campaign) => campaign.name),
      type: 'category',
    },
    yAxis: [valueAxis(true), valueAxis(false)],
  }

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className={styles.title}>{COPY.title}</h3>
          <p className={styles.subtitle}>{COPY.subtitle}</p>
        </div>

        <div className={styles.switch}>
          {(
            [
              ['sentiment', COPY.bySentiment],
              ['platform', COPY.byPlatform],
            ] as [Mode, string][]
          ).map(([value, label]) => (
            <button
              className={cn(
                styles.switchOption,
                mode === value && styles.switchOptionActive
              )}
              key={value}
              onClick={() => setMode(value)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'platform' ? (
        <PlatformBreakdown
          campaigns={campaigns}
          platforms={byPlatform.platforms}
          rows={byPlatform.rows}
        />
      ) : (
        <div className={styles.body}>
          <EChart height={CHART_HEIGHT} options={options} width={CHART_WIDTH} />

          <aside className={styles.comments}>
            <h4 className={styles.commentsTitle}>{COPY.topComments}</h4>

            <div className={styles.commentList}>
              {topComments.map((comment) => (
                <article className={styles.comment} key={comment.id}>
                  <header className={styles.commentHead}>
                    <span className={styles.avatar}>
                      {authorOf(comment.author).charAt(0).toUpperCase()}
                    </span>

                    <span className={styles.commentWho}>
                      <TruncatedTooltip
                        className={styles.commentAuthor}
                        text={authorOf(comment.author)}
                      />
                      <span className={styles.commentAge}>{comment.age}</span>
                    </span>

                    <span className={cn(styles.tone, styles[comment.tone])}>
                      {comment.tone.charAt(0).toUpperCase() +
                        comment.tone.slice(1)}
                    </span>
                  </header>

                  {/* No like count: a stored comment carries its author, its
                      text, its platform and its sentiment, and nothing that
                      counts engagement on the comment itself.

                      Clamped to four lines with the rest on hover: comments
                      run from one word to a paragraph, and the rail cannot be
                      sized for the longest of them. */}
                  <TruncatedTooltip
                    className={styles.commentText}
                    maxLines={COMMENT_LINES}
                    text={`“${comment.text}”`}
                  />
                </article>
              ))}
            </div>
          </aside>
        </div>
      )}

      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />

        <div className={styles.insightText}>
          <b className={styles.insightTitle}>{COPY.insight}</b>
          <p className={styles.insightBody}>
            {insight.split(FIGURE_SPLIT).map((part, index) =>
              FIGURE.test(part) ? (
                // The split keeps the separators, so odd indices are the
                // figures themselves. Keyed by position: the same number can
                // appear twice in one insight.
                // eslint-disable-next-line react/no-array-index-key
                <b className={styles.figure} key={index}>
                  {part}
                </b>
              ) : (
                part
              )
            )}
          </p>
        </div>
      </div>
    </section>
  )
}
